// @wc-ignore-file
// (RPC names and error text here are data for logs, not user-facing copy.)
import type {
  ChangeRequest,
  Container,
  CrDependencyListRow,
  CrEventListRow,
  CrJiraIssueLink,
  CrLinearIssueLink,
  CrNotionReference,
  Database,
  IncognitoMessageAttachment,
  Project,
  Workspace,
} from "@shared/types/supabase.types";

import type { Task } from "@/features/tasks/tasks.types";
import { supabase } from "@/lib/supabase";

/**
 * CR-116 — privileged, platform-admin-only reads. Every call writes one
 * `superuser_access_log` row server-side, so the query options in
 * `incognito-keys.ts` keep these cached rather than refetching on focus.
 * Never seed `containerQueries` caches with these results.
 */
/**
 * A human member or pending invite (email or domain) of a workspace, as the
 * admin-only members RPC returns it. Deliberately has no invite code: the RPC
 * never returns one (an invite code is an acceptance token, CR-114).
 */
export type IncognitoWorkspaceMember =
  Database["public"]["Functions"]["list_incognito_workspace_members"]["Returns"][number];

/**
 * CR-165 — a project member: a user, a pending invite, or (Q4) an AI teammate
 * carrying only its display name and avatar (never its system prompt).
 */
export type IncognitoProjectMember =
  Database["public"]["Functions"]["list_incognito_project_members"]["Returns"][number];

/** Either members list; the display helpers take both. */
export type IncognitoMember = IncognitoWorkspaceMember | IncognitoProjectMember;

export async function getIncognitoWorkspaceBySlug(slug: string): Promise<Workspace> {
  const { data, error } = await supabase
    .rpc("get_incognito_workspace_by_slug", { p_slug: slug })
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to open workspace "${slug}" incognito: ${error.message}`);
  }
  if (!data) {
    throw new Error(`Workspace "${slug}" not found`);
  }

  return data as Workspace;
}

export async function listIncognitoWorkspaceProjects(workspaceId: string): Promise<Project[]> {
  const { data, error } = await supabase.rpc("list_incognito_workspace_projects", {
    p_workspace_id: workspaceId,
  });

  if (error) {
    throw new Error(`Failed to list workspace projects incognito: ${error.message}`);
  }

  return (data ?? []) as Project[];
}

export async function listIncognitoWorkspaceMembers(
  workspaceId: string,
): Promise<IncognitoWorkspaceMember[]> {
  const { data, error } = await supabase.rpc("list_incognito_workspace_members", {
    p_workspace_id: workspaceId,
  });

  if (error) {
    throw new Error(`Failed to list workspace members incognito: ${error.message}`);
  }

  return data ?? [];
}

/**
 * CR-165 — the content container types the incognito project view reads, all
 * in ONE call: every call writes an audit row, so the sidebar and the list
 * pages partition this single cached result client-side. `file` is fetched
 * now (same call, no extra audit row) for the stage 4 CR detail's uploads.
 */
export const INCOGNITO_PROJECT_CONTAINER_TYPES = [
  "document",
  "room",
  "message_room",
  "file",
] as const satisfies readonly Database["public"]["Enums"]["container_type"][];

export async function getIncognitoProjectBySlugs(
  workspaceSlug: string,
  projectSlug: string,
): Promise<Project> {
  const { data, error } = await supabase
    .rpc("get_incognito_project_by_slugs", {
      p_workspace_slug: workspaceSlug,
      p_project_slug: projectSlug,
    })
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to open project "${projectSlug}" incognito: ${error.message}`);
  }
  if (!data) {
    throw new Error(`Project "${projectSlug}" not found`);
  }

  return data as Project;
}

/** Shared content containers of a project (Q7: never a private one). */
export async function listIncognitoProjectContainers(projectId: string): Promise<Container[]> {
  const { data, error } = await supabase.rpc("list_incognito_project_containers", {
    p_project_id: projectId,
    p_container_types: [...INCOGNITO_PROJECT_CONTAINER_TYPES],
  });

  if (error) {
    throw new Error(`Failed to list project contents incognito: ${error.message}`);
  }

  // Set-returning: the generated array type keeps `comment_target` as a
  // plain string (MergeDeep replaces arrays wholesale, CR-129); the column's
  // CHECK guarantees the narrow union.
  return (data ?? []) as Container[];
}

export async function listIncognitoProjectChangeRequests(
  projectId: string,
): Promise<ChangeRequest[]> {
  const { data, error } = await supabase.rpc("list_incognito_project_change_requests", {
    p_project_id: projectId,
  });

  if (error) {
    throw new Error(`Failed to list project change requests incognito: ${error.message}`);
  }

  // Set-returning: same generated-array widening for `priority`.
  return (data ?? []) as ChangeRequest[];
}

/**
 * CR-165 — every task of the project's shared documents, for all assignees
 * (no per-user clamp), as `get_project_tasks` shapes a row.
 */
export async function listIncognitoProjectTasks(projectId: string): Promise<Task[]> {
  const { data, error } = await supabase.rpc("list_incognito_project_tasks", {
    p_project_id: projectId,
  });

  if (error) {
    throw new Error(`Failed to list project tasks incognito: ${error.message}`);
  }

  return data ?? [];
}

/** CR-165 — the project's members (users, invites without code, AI teammates). */
export async function listIncognitoProjectMembers(
  projectId: string,
): Promise<IncognitoProjectMember[]> {
  const { data, error } = await supabase.rpc("list_incognito_project_members", {
    p_project_id: projectId,
  });

  if (error) {
    throw new Error(`Failed to list project members incognito: ${error.message}`);
  }

  return data ?? [];
}

/**
 * CR-165 — the latest stored version of a shared project document (Q2: a
 * snapshot, not the live Hocuspocus state — there is no read-only connection).
 * `null` when the document is not reachable (missing, private, deleted, not a
 * direct child of a live project); `data` is `null` for a document that has no
 * stored version yet.
 */
export type IncognitoDocumentContent = {
  documentId: string;
  data: string | null;
  firstUpdateAt: string | null;
};

export async function getIncognitoDocumentContent(
  documentId: string,
): Promise<IncognitoDocumentContent | null> {
  const { data, error } = await supabase
    .rpc("get_incognito_document_content", { p_document_id: documentId })
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to read document content incognito: ${error.message}`);
  }
  if (!data) {
    return null;
  }

  // The generated row type says `string`, but both columns are NULL for a
  // document without a stored version (one row, NULL data).
  const row = data as { document_id: string; data: string | null; first_update_at: string | null };
  return { documentId: row.document_id, data: row.data, firstUpdateAt: row.first_update_at };
}

/**
 * CR-148 — a change request's dependency rows (active and history, oldest
 * first), as `list_cr_dependencies` returns them. Archived dependency CRs are
 * left out server-side.
 */
export async function listIncognitoCrDependencies(crId: string): Promise<CrDependencyListRow[]> {
  const { data, error } = await supabase.rpc("list_incognito_cr_dependencies", {
    p_cr_id: crId,
  });

  if (error) {
    throw new Error(`Failed to list change request dependencies incognito: ${error.message}`);
  }

  return data ?? [];
}

/**
 * CR-230 (Q9) — a change request's Notion references, read-only. Logged as
 * `cr_notion_references_read` by the RPC. The rows are external links only.
 */
export async function listIncognitoCrNotionReferences(
  changeRequestId: string,
): Promise<CrNotionReference[]> {
  const { data, error } = await supabase.rpc("list_incognito_cr_notion_references", {
    p_change_request_id: changeRequestId,
  });

  if (error) {
    throw new Error(`Failed to list change request Notion pages incognito: ${error.message}`);
  }

  return (data ?? []) as CrNotionReference[];
}

/**
 * CR-229 (Q8) — a change request's Jira issue links, read-only. Logged by the
 * RPC. The rows are external links only.
 */
export async function listIncognitoCrJiraLinks(
  changeRequestId: string,
): Promise<CrJiraIssueLink[]> {
  const { data, error } = await supabase.rpc("list_incognito_cr_jira_links", {
    p_change_request_id: changeRequestId,
  });

  if (error) {
    throw new Error(`Failed to list change request Jira issues incognito: ${error.message}`);
  }

  return (data ?? []) as CrJiraIssueLink[];
}

/**
 * CR-239 (Q8) — a change request's Linear issue links, read-only. Logged by
 * the RPC (superuser_access_log action cr_linear_links_read). External links only.
 */
export async function listIncognitoCrLinearLinks(
  changeRequestId: string,
): Promise<CrLinearIssueLink[]> {
  const { data, error } = await supabase.rpc("list_incognito_cr_linear_links", {
    p_change_request_id: changeRequestId,
  });

  if (error) {
    throw new Error(`Failed to list change request Linear issues incognito: ${error.message}`);
  }

  return (data ?? []) as CrLinearIssueLink[];
}

/** CR-165 — a change request's event feed (newest first), as `list_cr_events` returns it. */
export async function listIncognitoCrEvents(crId: string): Promise<CrEventListRow[]> {
  const { data, error } = await supabase.rpc("list_incognito_cr_events", { p_cr_id: crId });

  if (error) {
    throw new Error(`Failed to list change request activity incognito: ${error.message}`);
  }

  return data ?? [];
}

/**
 * CR-165 — one message of a shared chat or message room, oldest first.
 * Attachments are names only (Q5): the RPC never returns a storage path, so
 * the page cannot build a download link.
 */
export type IncognitoRoomMessage =
  Database["public"]["Functions"]["list_incognito_room_messages"]["Returns"][number];

/**
 * Keeps only well-formed attachment entries: the column is `jsonb` on the
 * wire, so its shape is checked here rather than trusted (the type override
 * in supabase.types.ts is a compile-time promise only).
 */
export function parseIncognitoAttachments(value: unknown): IncognitoMessageAttachment[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const attachments: IncognitoMessageAttachment[] = [];
  for (const entry of value) {
    if (typeof entry !== "object" || entry === null) {
      continue;
    }
    const record = entry as Record<string, unknown>;
    if (typeof record.id !== "string" || typeof record.file_name !== "string") {
      continue;
    }
    const size = record.size_bytes;
    attachments.push({
      id: record.id,
      file_name: record.file_name,
      mime_type: typeof record.mime_type === "string" ? record.mime_type : null,
      // 0 = unknown: the page then shows the name without a size.
      size_bytes: typeof size === "number" && Number.isFinite(size) && size > 0 ? size : 0,
    });
  }
  return attachments;
}

export async function listIncognitoRoomMessages(roomId: string): Promise<IncognitoRoomMessage[]> {
  const { data, error } = await supabase.rpc("list_incognito_room_messages", {
    p_room_id: roomId,
  });

  if (error) {
    throw new Error(`Failed to read chat messages incognito: ${error.message}`);
  }

  return (data ?? []).map((row) => ({
    ...row,
    attachments: parseIncognitoAttachments(row.attachments as unknown),
  }));
}
